import { ALL_APP_LOCALES, type AppLocale } from "@/lib/i18n/constants";
import { isReservedRouteSegment } from "@/lib/routes/registry";
import { assertMarketingTheme } from "./theme";
import type { PersonaConfig, PersonaId } from "./types";

const PERSONA_EVIDENCE_DATE = "2026-06-22";
const PERSONA_EVIDENCE_SOURCE = "personalization-audit engine/page_persona_fit.csv plus Q1-Q2 2026 quote-history pricing note";
const HIGH_DEMAND_LOCALES_BY_PERSONA = {
  adult: ["en", "es", "pt-br", "ru", "ja"],
  agency: ["en", "de", "fr", "es"],
  creator: ["en", "es", "pt-br", "ru", "ja"],
  enterprise: ["en", "de", "fr", "es"],
  franchise: ["en", "es", "pt-br"],
} as const satisfies Record<PersonaId, readonly AppLocale[]>;

export const PERSONAS = [
  {
    id: "agency",
    slug: "agencies",
    label: "Agencies",
    selectorLabel: "Agency partners",
    description: "White-label and agency-safe delivery paths for teams that resell or coordinate SEOH work.",
    visibility: "public",
    slugLocked: true,
    indexable: true,
    includeInSitemap: true,
    publicRoutesEnabled: true,
    localizedSlugPolicy: "enabled",
    locales: ALL_APP_LOCALES,
    evidence: personaEvidence(
      "audit_research_only",
      "Agency appears as a high-leverage segment in the audit matrix, but live treatment launch still needs a dated non-PII traffic and outcome export.",
    ),
    gate: {
      type: "owner-approval",
      note: "Owner-approved for public persona indexing with conservative proof language.",
    },
    liveTargeting: personaLiveTargeting("agency"),
    theme: {
      "--marketing-accent": "#0d66b3",
      "--marketing-accent-strong": "#0a4f8c",
      "--marketing-accent-soft": "rgba(15, 108, 189, 0.12)",
    },
  },
  {
    id: "franchise",
    slug: "franchise",
    label: "Franchise and multi-location",
    selectorLabel: "Franchise teams",
    description: "Location, franchise, and distributed-brand SEO paths for teams that need repeatable local execution.",
    visibility: "public",
    slugLocked: true,
    indexable: true,
    includeInSitemap: true,
    publicRoutesEnabled: true,
    localizedSlugPolicy: "enabled",
    locales: ALL_APP_LOCALES,
    evidence: personaEvidence(
      "audit_research_only",
      "Franchise routes and angle briefs are present, but live treatment launch still needs persona-locale traffic viability and outcome evidence.",
    ),
    gate: {
      type: "owner-approval",
      note: "Owner-approved for public persona indexing and legacy redirect migration.",
    },
    liveTargeting: personaLiveTargeting("franchise"),
    theme: {
      "--marketing-accent": "#126a4a",
      "--marketing-accent-strong": "#0c5138",
      "--marketing-accent-soft": "rgba(18, 106, 74, 0.12)",
    },
  },
  {
    id: "creator",
    slug: "creators",
    label: "Creators",
    selectorLabel: "Creator teams",
    description: "Creator-economy search, reputation, and AI-video paths kept separate from adult route logic.",
    visibility: "public",
    slugLocked: true,
    indexable: true,
    includeInSitemap: true,
    publicRoutesEnabled: true,
    localizedSlugPolicy: "enabled",
    locales: ALL_APP_LOCALES,
    evidence: personaEvidence(
      "audit_research_only",
      "Creator copy research is route-backed and kept distinct from adult routing, but live treatment launch still needs observed conversion evidence.",
    ),
    gate: {
      type: "owner-approval",
      note: "Owner-approved for public persona indexing with brand-safe creator positioning.",
    },
    liveTargeting: personaLiveTargeting("creator"),
    theme: {
      "--marketing-accent": "#6f4bb5",
      "--marketing-accent-strong": "#55358f",
      "--marketing-accent-soft": "rgba(111, 75, 181, 0.12)",
    },
  },
  {
    id: "adult",
    slug: "adult",
    label: "Adult industry",
    selectorLabel: "Lawful 18+ businesses",
    description: "Lawful 18+, B2B, compliance-first adult marketing paths with an explicit route-ownership gate.",
    visibility: "public",
    slugLocked: true,
    indexable: true,
    includeInSitemap: true,
    publicRoutesEnabled: true,
    localizedSlugPolicy: "enabled",
    locales: ALL_APP_LOCALES,
    evidence: personaEvidence(
      "blocked_live_data",
      "Adult persona routes are lawful 18+ and compliance-gated; no adult treatment may enter nurture or launch before marketing_compliance_gate=passed.",
    ),
    gate: {
      type: "adult-age",
      note: "Owner-approved for lawful 18+ B2B public indexing with conservative non-explicit wording.",
    },
    liveTargeting: personaLiveTargeting("adult"),
    theme: {
      "--marketing-accent": "#b83f6a",
      "--marketing-accent-strong": "#8f284d",
      "--marketing-accent-soft": "rgba(184, 63, 106, 0.12)",
    },
  },
  {
    id: "enterprise",
    slug: "enterprise",
    label: "Enterprise",
    selectorLabel: "Enterprise teams",
    description: "Governance, reporting, compliance, and multi-stakeholder search programs for larger teams.",
    visibility: "public",
    slugLocked: true,
    indexable: true,
    includeInSitemap: true,
    publicRoutesEnabled: true,
    localizedSlugPolicy: "enabled",
    locales: ALL_APP_LOCALES,
    evidence: personaEvidence(
      "audit_research_only",
      "Enterprise routes map to governed, sensitive, and multi-stakeholder work, but live treatment launch still needs traffic and quote/outcome support.",
    ),
    gate: {
      type: "owner-approval",
      note: "Owner-approved for public persona indexing with governed-enterprise positioning.",
    },
    liveTargeting: personaLiveTargeting("enterprise"),
    theme: {
      "--marketing-accent": "#4f647d",
      "--marketing-accent-strong": "#39495d",
      "--marketing-accent-soft": "rgba(79, 100, 125, 0.12)",
    },
  },
] as const satisfies readonly PersonaConfig[];

const APPROVED_RESERVED_SLUG_ALLOWLIST = new Set<PersonaId>(["adult"]);

export type PersonaReservedSlugCollision = {
  persona: PersonaConfig;
  slug: string;
  stagedException: boolean;
};

assertPersonaRegistry(PERSONAS);

export function getPersonas(): readonly PersonaConfig[] {
  return PERSONAS;
}

export function getPersonaById(id: PersonaId): PersonaConfig {
  const persona = PERSONAS.find((candidate) => candidate.id === id);

  if (!persona) {
    throw new Error(`Unknown persona id "${id}".`);
  }

  return persona;
}

export function getPersonaSlugs(personas: readonly PersonaConfig[] = PERSONAS): string[] {
  return personas.map((persona) => persona.slug);
}

export function findPersonaReservedSlugCollisions(
  personas: readonly PersonaConfig[] = PERSONAS,
): PersonaReservedSlugCollision[] {
  return personas
    .filter((persona) => isReservedRouteSegment(persona.slug))
    .map((persona) => ({
      persona,
      slug: persona.slug,
      stagedException: isApprovedReservedSlug(persona),
    }));
}

export function findUnsafePersonaReservedSlugCollisions(personas: readonly PersonaConfig[] = PERSONAS): PersonaReservedSlugCollision[] {
  return findPersonaReservedSlugCollisions(personas).filter((collision) => !collision.stagedException);
}

export function assertPersonaRegistry(personas: readonly PersonaConfig[] = PERSONAS): void {
  const ids = new Set<PersonaId>();
  const slugs = new Set<string>();

  for (const persona of personas) {
    if (ids.has(persona.id)) {
      throw new Error(`Duplicate persona id "${persona.id}".`);
    }

    if (slugs.has(persona.slug)) {
      throw new Error(`Duplicate persona slug "${persona.slug}".`);
    }

    ids.add(persona.id);
    slugs.add(persona.slug);
    assertPersonaEvidence(persona);
    assertPersonaLiveTargeting(persona);
    assertMarketingTheme(persona.theme, `${persona.id} persona theme`);
  }

  assertNoUnsafePersonaSlugCollisions(personas);
  assertNoUnlockedIndexablePersonas(personas);
}

function personaEvidence(status: PersonaConfig["evidence"]["status"], note: string): PersonaConfig["evidence"] {
  return {
    date: PERSONA_EVIDENCE_DATE,
    source: PERSONA_EVIDENCE_SOURCE,
    status,
    note,
  };
}

function personaLiveTargeting(personaId: PersonaId): PersonaConfig["liveTargeting"] {
  return {
    unit: "visitor",
    level: "member_segment",
    maxLiveCells: 5,
    viableLocales: HIGH_DEMAND_LOCALES_BY_PERSONA[personaId],
    evidenceRequiredBeforeTreatment: true,
    prolificPersonasPolicy: "research_only",
    note:
      "Public routes remain available in all locales, but live personalization targets only the five member-segments and the listed high-demand locales until evidence clears.",
  };
}

function assertPersonaEvidence(persona: PersonaConfig): void {
  if (!persona.evidence.date || !persona.evidence.source || !persona.evidence.note) {
    throw new Error(`Persona "${persona.id}" is missing dated evidence notes.`);
  }
}

function assertPersonaLiveTargeting(persona: PersonaConfig): void {
  if (
    persona.liveTargeting.unit !== "visitor" ||
    persona.liveTargeting.level !== "member_segment" ||
    persona.liveTargeting.maxLiveCells !== 5 ||
    persona.liveTargeting.prolificPersonasPolicy !== "research_only" ||
    !persona.liveTargeting.evidenceRequiredBeforeTreatment
  ) {
    throw new Error(`Persona "${persona.id}" has unsafe live targeting policy.`);
  }

  if (persona.liveTargeting.viableLocales.some((locale) => !ALL_APP_LOCALES.includes(locale))) {
    throw new Error(`Persona "${persona.id}" has an unsupported viable locale.`);
  }
}

export function assertNoUnsafePersonaSlugCollisions(personas: readonly PersonaConfig[] = PERSONAS): void {
  const collisions = findUnsafePersonaReservedSlugCollisions(personas);

  if (collisions.length > 0) {
    throw new Error(`Persona slugs collide with reserved routes: ${collisions.map((collision) => collision.slug).join(", ")}.`);
  }
}

export function assertNoUnlockedIndexablePersonas(personas: readonly PersonaConfig[] = PERSONAS): void {
  const unsafe = personas.filter(
    (persona) =>
      persona.indexable &&
      (!persona.slugLocked || !persona.publicRoutesEnabled || !persona.includeInSitemap || persona.visibility !== "public"),
  );

  if (unsafe.length > 0) {
    throw new Error(`Unlocked or staged personas cannot become indexable: ${unsafe.map((persona) => persona.id).join(", ")}.`);
  }
}

function isApprovedReservedSlug(persona: PersonaConfig): boolean {
  return (
    APPROVED_RESERVED_SLUG_ALLOWLIST.has(persona.id) &&
    persona.indexable &&
    persona.slugLocked &&
    persona.publicRoutesEnabled &&
    persona.includeInSitemap &&
    persona.visibility === "public"
  );
}
